export const VALID_ID_SEGMENT_REGEX = /^[\w.-]+$/;

export const validateIdSegmentOrThrow = (idSegment: string): void => {
  if (!VALID_ID_SEGMENT_REGEX.test(idSegment)) {
    throw new Error(
      `Invalid ID segment: ${idSegment}. It must match the regex ${VALID_ID_SEGMENT_REGEX}.`,
    );
  }
};

export const uuidv4 = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    // Use crypto.getRandomValues() if available
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);

    // Set the version (4) and variant (10xx)
    array[6] = (array[6] & 0x0F) | 0x40; // Version 4
    array[8] = (array[8] & 0x3F) | 0x80; // Variant 10xx

    // Convert to UUID format
    return [...array]
      .map((byte, index) => {
        const hex = byte.toString(16).padStart(2, '0');
        return [4, 6, 8, 10].includes(index) ? `-${hex}` : hex;
      })
      .join('');
  }
  else {
    // Fallback to Math.random() if crypto is not available
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0; // Generate a random number between 0 and 15
      const value = char === 'x' ? random : (random & 0x3) | 0x8; // Set the correct bits for 'y'
      return value.toString(16); // Convert to hexadecimal
    });
  }
};

export class LeisoftAasId {
  domain?: string;
  idType?: 'asset' | 'shell' | 'submodel' | 'concept-description';
  id?: string;
  units?: string[];

  toUrn(): string {
    let urn = 'urn:';
    if (this.domain) {
      validateIdSegmentOrThrow(this.domain);
      urn += `${this.domain}:`;
    }
    else {
      urn += 'client.leisoft.de:';
    }
    if (this.units) {
      this.units.forEach((unit) => {
        validateIdSegmentOrThrow(unit);
        urn += `${unit}:`;
      });
    }
    switch (this.idType) {
      case 'asset':
        urn += 'assets';
        break;
      case 'shell':
        urn += 'shells';
        break;
      case 'submodel':
        urn += 'submodels';
        break;
      case 'concept-description':
        urn += 'concept_descriptions';
        break;
      default:
        urn += 'entities';
        break;
    }
    urn += ':ids:';
    if (this.id) {
      validateIdSegmentOrThrow(this.id);
      urn += this.id;
    }
    else {
      urn += uuidv4();
    }
    return urn;
  }

  toUrl(protocol: 'http' | 'https' | 'ftp' | 'ftps' | 'sftp' | 'file'): string {
    let url = `${protocol}://`;
    if (this.domain) {
      validateIdSegmentOrThrow(this.domain);
      url += `${this.domain}/`;
    }
    else if (protocol === 'file') {
      url += '/';
    }
    else {
      url += 'client.leisoft.de/';
    }
    if (this.units) {
      this.units.forEach((unit) => {
        validateIdSegmentOrThrow(unit);
        url += `${unit}/`;
      });
    }
    switch (this.idType) {
      case 'asset':
        url += 'assets';
        break;
      case 'shell':
        url += 'shells';
        break;
      case 'submodel':
        url += 'submodels';
        break;
      case 'concept-description':
        url += 'concept_descriptions';
        break;
      default:
        url += 'entities';
        break;
    }
    url += '/ids/';
    if (this.id) {
      validateIdSegmentOrThrow(this.id);
      url += this.id;
    }
    else {
      url += uuidv4();
    }
    return url;
  }

  static from(other: LeisoftAasId): LeisoftAasId {
    const newId = new LeisoftAasId();
    newId.domain = other.domain;
    newId.idType = other.idType;
    newId.id = other.id;
    newId.units = other.units ? [...other.units] : undefined;
    return newId;
  }
}
